import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useRef } from "react";
import { useGetAccessTokenSilentlyCallback } from "../../auth";
import { sendLoggingEvents, TrackingEvents } from "../../events";
import { audioRecordingRecoveryGate } from "../../statsig/gates";
import { Statsig } from "../../statsig/StatsigProvider";
import {
  AudioRecordingState,
  recordingStateAtom,
  useStartAudioRecording,
} from "../../video-sdk";
import { hasRecordingAtom } from "./state";
import { useAudioRecorderLifecycleCallbacks } from "./useAudioRecorderLifecycleCallbacks";
import { useHandleAudioRecordingRecoveryCallback } from "./useHandleAudioRecordingRecoveryCallback";
import { useUploadAudioCallback } from "./useUploadAudioCallback";
import { useCanProviderUseTranscriptionCallback } from "./useCanProviderUseTranscriptionCallback";

export function useStartRecordingCallback() {
  const getCanProviderUseTranscription =
    useCanProviderUseTranscriptionCallback();

  const canStartRecording = useAtomCallback(
    useCallback(
      (get) => {
        const recordingIsActive =
          get(recordingStateAtom).value === AudioRecordingState.RECORDING;
        const canUseTranscription = getCanProviderUseTranscription();
        return canUseTranscription && !recordingIsActive;
      },
      [getCanProviderUseTranscription],
    ),
  );
  const setHasRecording = useSetAtom(hasRecordingAtom);

  return useStartAudioRecording({
    canStart: canStartRecording,
    handleStart: useStartRecordingTwilioAudio(),
    handleAfterStart: function afterStart() {
      setHasRecording(true);
    },
  });
}

/**
 * Creates a callback that handles the complete Twilio audio recording setup and start process.
 *
 * This hook orchestrates the entire audio recording workflow for Twilio sessions:
 * 1. Ensures user authentication is valid
 * 2. Sets up the recording environment (AudioContext, MediaStream, AudioRecorder)
 * 3. Starts recording with chunked upload to S3
 * 4. Handles recovery scenarios for failed recordings
 *
 * IMPORTANT: For multi-participant recording, you must also use `useCombineAudioTracks`
 * to combine audio from all participants into the recording stream.
 */
function useStartRecordingTwilioAudio() {
  const isRecordingPermissionDeniedRef = useRef(false);
  const uploadAudio = useUploadAudioCallback();
  const getAccessTokenSilently = useGetAccessTokenSilentlyCallback();
  const handleAudioRecordingRecovery =
    useHandleAudioRecordingRecoveryCallback();
  const { setUpRecording } = useAudioRecorderLifecycleCallbacks();

  return useCallback(async () => {
    await getAccessTokenSilently({
      showToast: true,
    });

    const { audioRecorder } = await setUpRecording();
    await audioRecorder.startRecording({
      onIntervalProc: async function uploadAudioChunk({
        audioBlob,
        recordingMetadata,
      }) {
        const isRecordingAudioContextPermissionDenied = !audioBlob.size;
        if (isRecordingAudioContextPermissionDenied) {
          isRecordingPermissionDeniedRef.current = true;
          sendLoggingEvents(
            TrackingEvents.AUDIO_RECORDING_PERMISSION_DENIED,
            recordingMetadata,
            {
              logLevel: "warn",
              message: "Audio blob size is 0, skipping upload",
            },
          );
          if (Statsig.checkGate(audioRecordingRecoveryGate)) {
            await handleAudioRecordingRecovery();
          }
          return;
        }

        sendLoggingEvents(
          TrackingEvents.UPLOADING_AUDIO_RECORDING_CHUNK,
          {
            ...recordingMetadata,
            recovered: isRecordingPermissionDeniedRef.current,
          },
          {
            logLevel: "info",
            message: "Uploading audio recording chunk",
          },
        );
        isRecordingPermissionDeniedRef.current = false;

        await uploadAudio({ audioBlob, recordingMetadata });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
